import type { FC } from 'react';
import React from 'react';
import { defineMessages, useIntl } from 'react-intl-next';

import { Box, Text, Stack, Inline } from '@atlaskit/primitives/compiled';
import { Field } from '@atlaskit/form';
import Range from '@atlaskit/range';

import { useAudioBriefing } from '../../../../hooks/useAudioBriefing';
import type { CustomLengthType } from '../../../../types/customizationTypes';
import { CustomLength } from '../../../../types/customizationTypes';

const i18n = defineMessages({
	audioDurationLabel: {
		id: 'audio.audio-briefing-popup.customize-tab.duration-label',
		defaultMessage: 'Roughly how long is the audio?',
		description: 'Label for a Select component used for selecting audio duration',
	},
	shortDurationLabel: {
		id: 'audio.audio-briefing-popup.customize-tab.duration.short',
		defaultMessage: 'Shorter',
		description: 'Label for a shorter length audio option',
	},
	mediumDurationLabel: {
		id: 'audio.audio-briefing-popup.customize-tab.duration.medium',
		defaultMessage: 'Medium',
		description: 'Label for a medium length audio option',
	},
	longDurationLabel: {
		id: 'audio.audio-briefing-popup.customize-tab.duration.long',
		defaultMessage: 'Longer',
		description: 'Label for a longer length audio option',
	},
});

interface DurationOption {
	label: keyof typeof i18n;
	value: CustomLengthType;
}

const durationOptions: DurationOption[] = [
	{
		label: 'shortDurationLabel',
		value: CustomLength.SHORT,
	},
	{
		label: 'mediumDurationLabel',
		value: CustomLength.MEDIUM,
	},
	{
		label: 'longDurationLabel',
		value: CustomLength.LONG,
	},
];

export const DurationSelector: FC = () => {
	const { formatMessage } = useIntl();
	const [{ customization }] = useAudioBriefing();
	const { length } = customization;

	return (
		<Field name="length" defaultValue={length} label={formatMessage(i18n.audioDurationLabel)}>
			{({ fieldProps: { value: customLength, onChange } }) => {
				const rangeValue = durationOptions.findIndex((option) => option.value === customLength);

				const handleRangeChange = (newRangeValue: number) => {
					onChange(durationOptions[newRangeValue].value);
				};

				return (
					<Box>
						<Stack grow="fill">
							<Range
								step={1}
								min={0}
								max={2}
								value={rangeValue}
								onChange={handleRangeChange}
								testId="duration-range-slider"
							/>
							<Inline spread="space-between" grow="fill">
								{durationOptions.map((option, index) => (
									<Text
										key={option.value}
										size="small"
										color={rangeValue === index ? 'color.text' : 'color.text.subtlest'}
										weight={rangeValue === index ? 'bold' : 'regular'}
									>
										{formatMessage(i18n[option.label])}
									</Text>
								))}
							</Inline>
						</Stack>
					</Box>
				);
			}}
		</Field>
	);
};
